import { useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';
import { useObservable, useObservableState } from 'observable-hooks';
import { NOTIFICATION_VARIANT, useNotifications } from '../notifications/Notifications';
import type { ConfigHelper } from '../pages/measuringPoint/configHelper';
import { useFormStateObservable } from './utils';

// When using `root` as the error key, the errors are cleared on submission.
// See the rules section here:
// https://react-hook-form.com/docs/useform/seterror
export const NON_FIELD_ERRORS = 'root';

interface FieldError {
    message: string;
    type: string;
}

interface ErrorToNotificationsLinkProps {
    configHelper: ConfigHelper;
}

export function ErrorToNotificationsLink({ configHelper }: ErrorToNotificationsLinkProps) {
    const errors$ = useFormStateObservable('errors');
    const { addNotification } = useNotifications();

    const messages$ = useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap((inputs) => combineLatest(inputs)),
                map(([errors, availableFields]) =>
                    Object.entries(errors ?? {}).flatMap(([key, error]) => {
                        // When the `key` is `NON_FIELD_ERRORS`, the `error` is actually an
                        // object that could contain multiple errors.
                        if (key === NON_FIELD_ERRORS) {
                            return Object.values(error as Record<string, FieldError>).map(
                                (subError) => subError.message
                            );
                        }

                        const fieldError = error as FieldError;

                        // In normal situations, `key` is a field name. All the fields in the
                        // form will check if they need to display a related error themselves.
                        // In the past, we had problems with our validation validating fields
                        // that were hidden by the config helper (this has already been fixed).
                        // When this happened, the error could not be displayed near the field
                        // itself, leaving the UI in the dark. To prevent this, we will check
                        // if the field might be hidden by the config helper. If it is hidden,
                        // we will display the error at the top of the form. Even though the
                        // user might not know how to fix it, a screenshot sent to customer
                        // support will already contain a lot of info, instead of just a
                        // 'normal' screen where the save button appears to have no effect.
                        if (
                            configHelper.enabled &&
                            configHelper.allFields.includes(key) &&
                            !Object.keys(availableFields).includes(key)
                        ) {
                            return [`${key}: ${fieldError.message}`];
                        }

                        return [];
                    })
                ),
                // Emit only distinct values for efficiency.
                distinctUntilChanged<Array<string>>(isEqual)
            ),
        [errors$, configHelper.availableFields$]
    );

    const messages = useObservableState(messages$, []);

    useEffect(() => {
        const notifications = messages.map((message) =>
            addNotification({
                message,
                variant: NOTIFICATION_VARIANT.ERROR,
            })
        );

        return () => {
            notifications.forEach((notification) => {
                notification.remove();
            });
        };
    }, [messages, addNotification]);

    return null;
}
